import { Injectable } from '@angular/core';
import { RequestService } from '@df/api/request/request.service';
import { DfEventService } from '@df/core/service/df-event';
import { TimeoutService } from '@df/ng/timeout.service';
import { SearchClearEvent } from '@df/search/search-clear-event';
import { SearchEvent } from '@df/search/search-event';
import { SearchResult } from '@df/search/search-result';
import { SearchResultEvent } from '@df/search/search-result-event';
import { SearchResultFactoryService } from '@df/search/search-result-factory.service';
import { Search } from '@df/search/search.interfaces';
import { Subject, Subscription } from 'rxjs';

declare module '@df/core/service/df-event' {
    interface IEventType {
        // basket
        dfSearch: DfEventConfig<'dfSearch', Search.IRequest, SearchResult>;
    }
}

@Injectable()
export abstract class SearchService {
    /**
     * Search events subject
     */
    protected searchSubject = new Subject<SearchEvent>();

    
    constructor(
        protected requestService: RequestService,
        protected searchResultFactoryService: SearchResultFactoryService,
        protected timeoutService: TimeoutService,
        private dfEventService: DfEventService
    ) {}

    /**
     * Does the search including models loading
     */
    search(request: Search.IRequest): Promise<SearchResult> {
        return this.dfEventService.dispatch<SearchResult>({
            id: 'dfSearch',
            data: request,
            action: async data => {
                const response: Search.IResult = await this.doSearch(data);
                const searchResult = this.searchResultFactoryService.create(data.q, response, data);
                await searchResult.load();
                return searchResult;
            }
        });
    }

    /**
     * Sends next result event
     */
    next(searchId: string, searchResult: SearchResult | undefined): void {
        this.searchSubject.next(new SearchResultEvent(searchId, searchResult));
    }

    /**
     * Sends clearing event
     */
    clear(searchId?: string): void {
        if (searchId) {
            this.searchSubject.next(new SearchClearEvent(searchId));
        }
    }

    subscribeToSearch(...args): Subscription {
        return this.searchSubject.subscribe(...args);
    }

    /**
     * Sends search request
     */
    protected abstract doSearch(request: Search.IRequest): Promise<Search.IResult>;
}
